import React, { useState } from 'react';
import { Upload, Shield, AlertCircle, CheckCircle, XCircle, Loader2, FileText } from 'lucide-react';

const FileUploadScanner = () => {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const SCAN_URL = 'https://viruscan.horoboro.com/v2/scan';

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    setFile(selectedFile);
    setScanResult(null);
  };

  const scanFile = async () => {
    if (!file) return;
    
    setScanning(true);
    setScanResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add basic auth credentials
      const username = 'viruscan';
      const password = 'XZUcWtdtRczDrJKkoAxFR87XmnRfG7iT';
      const basicAuth = 'Basic ' + btoa(`${username}:${password}`);
      
      const response = await fetch(SCAN_URL, {
        method: 'POST',
        headers: {
          'Authorization': basicAuth
        },
        body: formData,
      });
      let result = {
        status: response.status,
        isClean: false,
        message: '',
        description: ''
      };
      
      if (response.status === 200) {
        result.isClean = true;
        result.message = 'File is clean';
        result.description = 'No threats detected';
      } else if (response.status === 406) {
        try {
          const data = await response.json();
          result.message = 'Virus detected!';
          result.description = data.Description || 'Unknown threat detected';
        } catch {
          result.message = 'Virus detected!';
          result.description = 'Threat detected but unable to parse details';
        }
      } else if (response.status === 400) {
        result.message = 'Scan error';
        result.description = 'ClamAV error while scanning file';
      } else if (response.status === 412) {
        result.message = 'Parse error';
        result.description = 'Unable to parse file';
      } else {
        result.message = 'Unexpected error';
        result.description = `Server returned status code: ${response.status}`;
      }
      
      setScanResult(result);
    } catch (error) {
      setScanResult({
        status: 0,
        isClean: false,
        message: 'Connection error',
        description: error.message || 'Failed to connect to scanning service'
      });
    } finally {
      setScanning(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const resetUpload = () => {
    setFile(null);
    setScanResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          <div className="flex items-center justify-center mb-8">
            <Shield className="w-10 h-10 text-purple-400 mr-3" />
            <h1 className="text-3xl font-bold text-white">Virus Scanner</h1>
          </div>
          
          {!file ? (
            <div onDragEnter={handleDrag}>
              <div
                className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ${
                  dragActive 
                    ? 'border-purple-400 bg-purple-400/10' 
                    : 'border-gray-400 hover:border-purple-400 hover:bg-white/5'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleChange}
                />
                
                <Upload className="w-16 h-16 mx-auto mb-4 text-purple-400" />
                
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer"
                >
                  <p className="text-xl font-semibold text-white mb-2">
                    Drop your file here
                  </p>
                  <p className="text-gray-300 mb-4">or</p>
                  <span className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 cursor-pointer">
                    Browse Files
                  </span>
                </label>
                
                <p className="mt-4 text-sm text-gray-400">
                  Supported formats: All file types
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <FileText className="w-10 h-10 text-purple-400 mt-1" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">
                        {file.name}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={resetUpload}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {!scanResult && (
                <button
                  onClick={scanFile}
                  disabled={scanning}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Scanning...</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      <span>Scan for Viruses</span>
                    </>
                  )}
                </button>
              )}
              
              {scanResult && (
                <div className={`rounded-lg p-6 border-2 ${
                  scanResult.isClean 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-start space-x-4">
                    {scanResult.isClean ? (
                      <CheckCircle className="w-8 h-8 text-green-400 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-red-400 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg ${
                        scanResult.isClean ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {scanResult.message}
                      </h3>
                      <p className="text-gray-300 mt-1">
                        {scanResult.description}
                      </p>
                      {!scanResult.isClean && scanResult.status !== 0 && (
                        <p className="text-sm text-gray-400 mt-2">
                          Status code: {scanResult.status}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={scanFile}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200"
                    >
                      Scan Again
                    </button>
                    <button
                      onClick={resetUpload}
                      className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
                    >
                      Upload New File
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            Powered by ClamAV • Secure file scanning
          </p>
        </div>
      </div>
    </div>
  );
};

export default FileUploadScanner;